import type { TargetAndTransition } from 'framer-motion';
import { WHILE_IN_VIEW_ROOT_MARGIN } from './motionViewport';

export function isSafariBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/** `section`: default bottom-inset margin. `nested`: `once` only. `{ margin }`: custom IO root. */
export type ScrollRevealMode = 'section' | 'nested' | { margin: string };

/**
 * Safari often misses Framer’s `whileInView` (IntersectionObserver) on SPA navigations; use
 * `animate` there so content appears on mount. Other browsers keep scroll-linked reveals.
 */
export function scrollReveal(
  isSafari: boolean,
  initial: TargetAndTransition,
  revealed: TargetAndTransition,
  forScroll: ScrollRevealMode = 'section'
) {
  if (isSafari) {
    return { initial, animate: revealed };
  }
  if (forScroll === 'section') {
    return {
      initial,
      whileInView: revealed,
      viewport: { once: true, margin: WHILE_IN_VIEW_ROOT_MARGIN },
    };
  }
  if (forScroll === 'nested') {
    return { initial, whileInView: revealed, viewport: { once: true } };
  }
  return {
    initial,
    whileInView: revealed,
    viewport: { once: true, margin: forScroll.margin },
  };
}

/** For `variants` + `whileInView="visible"` / `initial="hidden"`. */
export function scrollRevealVariant(
  isSafari: boolean,
  forScroll: ScrollRevealMode = 'section'
) {
  if (isSafari) {
    return { initial: 'hidden' as const, animate: 'visible' as const };
  }
  if (forScroll === 'section') {
    return {
      initial: 'hidden' as const,
      whileInView: 'visible' as const,
      viewport: { once: true, margin: WHILE_IN_VIEW_ROOT_MARGIN },
    };
  }
  if (forScroll === 'nested') {
    return {
      initial: 'hidden' as const,
      whileInView: 'visible' as const,
      viewport: { once: true },
    };
  }
  return {
    initial: 'hidden' as const,
    whileInView: 'visible' as const,
    viewport: { once: true, margin: forScroll.margin },
  };
}
